/**
 * 5. 메인 키워드 분석 카드
 * - 월간 검색량 (PC/Mobile/Total)
 * - 누적 콘텐츠 발행량 (블로그/카페/Total)
 * - CPC 광고 경쟁도 및 비율 (높음/중간/낮음)
 *
 * @param {object} report  - 분석 리포트 데이터 (report.keywordInfo 사용)
 * @param {string} keyword - 현재 분석 기준 키워드 (카드 타이틀 표시용)
 */
import React from 'react';
import { Card, Row, Col, Divider, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const KeywordAnalysisCard = ({ report, keyword }) => {
    return (
        <Card className="full-chart-card" bordered={false} bodyStyle={{ padding: '0 24px 24px' }}>
            <Divider
                orientation="left"
                orientationMargin={0}
                style={{ borderTopColor: '#3b82f6', borderTopWidth: 3, width: 20 }}
            >
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                    메인 키워드 분석{' '}
                    <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
                        &quot;{report.keywordInfo?.relKeyword || keyword}&quot; 기준
                    </span>
                </span>
            </Divider>
            <Row gutter={[16, 16]}>
                {/* 월간 검색량 */}
                <Col span={6}>
                    <div className="kw-section kw-section-search">
                        <div className="kw-section-header">
                            <span>월간 검색량</span>
                            <Tooltip title="최근 한달간 네이버를 이용한 사용자가 해당 키워드를 검색한 횟수입니다. (통합검색 기준)">
                                <InfoCircleOutlined
                                    style={{ fontSize: 12, opacity: 0.7, cursor: 'help' }}
                                />
                            </Tooltip>
                        </div>
                        <div className="kw-row">
                            <span className="kw-row-label">PC</span>
                            <span className="kw-row-value">
                                {(report.keywordInfo?.pc || 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="kw-row" style={{ marginBottom: 0 }}>
                            <span className="kw-row-label">모바일</span>
                            <span className="kw-row-value">
                                {(report.keywordInfo?.mobile || 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="kw-total-row">
                            <span className="kw-total-label">Total</span>
                            <span className="kw-total-value">
                                {(report.keywordInfo?.total || 0).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </Col>

                {/* 누적 콘텐츠 발행량 */}
                <Col span={6}>
                    <div className="kw-section kw-section-content">
                        <div className="kw-section-header">
                            <span>누적 콘텐츠 발행량</span>
                            <Tooltip title="네이버 블로그·카페에 해당 키워드로 작성된 누적 게시글 수입니다. 수치가 높을수록 콘텐츠 경쟁이 치열하며, 자연 유입 노출이 어려울 수 있습니다.">
                                <InfoCircleOutlined
                                    style={{ fontSize: 12, opacity: 0.7, cursor: 'help' }}
                                />
                            </Tooltip>
                        </div>
                        <div className="kw-row">
                            <span className="kw-row-label">블로그</span>
                            <span className="kw-row-value">
                                {(report.keywordInfo?.blogCount || 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="kw-row" style={{ marginBottom: 0 }}>
                            <span className="kw-row-label">카페</span>
                            <span className="kw-row-value">
                                {(report.keywordInfo?.cafeCount || 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="kw-total-row">
                            <span className="kw-total-label">Total</span>
                            <span className="kw-total-value">
                                {(
                                    (report.keywordInfo?.blogCount || 0) +
                                    (report.keywordInfo?.cafeCount || 0)
                                ).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </Col>

                {/* 월간 클릭 현황 */}
                <Col span={6}>
                    <div className="kw-section kw-section-click">
                        <div className="kw-section-header">
                            <span>월간 클릭 현황</span>
                            <Tooltip
                                title={
                                    <span>
                                        최근 한달간 해당 키워드를 검색했을 때 통합검색 영역에 노출된
                                        광고가 받은 평균 클릭수입니다.
                                        <br />
                                        괄호 안 수치는 클릭률(CTR)입니다.
                                    </span>
                                }
                            >
                                <InfoCircleOutlined
                                    style={{ fontSize: 12, opacity: 0.7, cursor: 'help' }}
                                />
                            </Tooltip>
                        </div>
                        <div className="kw-row">
                            <span className="kw-row-label">PC</span>
                            <span className="kw-row-value">
                                {Math.round(report.keywordInfo?.pcClick || 0).toLocaleString()}
                                <span className="kw-ctr-badge">
                                    ({report.keywordInfo?.pcCtr?.toFixed(2) || 0}%)
                                </span>
                            </span>
                        </div>
                        <div className="kw-row" style={{ marginBottom: 0 }}>
                            <span className="kw-row-label">모바일</span>
                            <span className="kw-row-value">
                                {Math.round(report.keywordInfo?.mobileClick || 0).toLocaleString()}
                                <span className="kw-ctr-badge">
                                    ({report.keywordInfo?.mobileCtr?.toFixed(2) || 0}%)
                                </span>
                            </span>
                        </div>
                        <div className="kw-total-row">
                            <span className="kw-total-label">Total</span>
                            <span className="kw-total-value">
                                {Math.round(report.keywordInfo?.totalClick || 0).toLocaleString()}
                                <span className="kw-ctr-badge">
                                    ({report.keywordInfo?.totalCtr?.toFixed(2) || 0}%)
                                </span>
                            </span>
                        </div>
                    </div>
                </Col>

                {/* CPC 광고 경쟁도 */}
                <Col span={6}>
                    {(() => {
                        const idx = report.keywordInfo?.compIdx || '중간';
                        const levels = ['낮음', '중간', '높음'];
                        const colors = { 낮음: '#10b981', 중간: '#f59e0b', 높음: '#ef4444' };
                        const color = colors[idx] || '#f59e0b';
                        return (
                            <div className="kw-section kw-section-cpc">
                                <div
                                    className="kw-section-header"
                                    style={{ justifyContent: 'center' }}
                                >
                                    <span>CPC 광고 경쟁도</span>
                                    <Tooltip title="최근 한달간 해당 키워드에 대한 광고 경쟁 강도를 PC 통합검색 기준으로 높음/중간/낮음으로 구분한 지표입니다. 광고주가 많을수록 경쟁도가 높습니다.">
                                        <InfoCircleOutlined
                                            style={{ fontSize: 12, opacity: 0.7, cursor: 'help' }}
                                        />
                                    </Tooltip>
                                </div>
                                <div className="kw-cpc-value" style={{ color }}>
                                    {idx}
                                </div>
                                {/* 3단계 게이지 바 */}
                                <div className="kw-gauge-wrap">
                                    {levels.map(l => (
                                        <div
                                            key={l}
                                            className="kw-gauge-bar"
                                            style={{
                                                background: idx === l ? colors[l] : '#e2e8f0',
                                            }}
                                        />
                                    ))}
                                </div>
                                <div className="kw-gauge-labels">
                                    {levels.map(l => (
                                        <span
                                            key={l}
                                            style={{
                                                fontWeight: idx === l ? 800 : 400,
                                                color: idx === l ? colors[l] : '#94a3b8',
                                            }}
                                        >
                                            {l}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </Col>
            </Row>
        </Card>
    );
};

export default KeywordAnalysisCard;
